/**
 * PublishFanoutService.ts
 *
 * Orchestrates a multi-variant publish command: expands a user's
 * `FanoutSelection` (languages × aspectRatios × resLevel) into N concurrent
 * renders, each with its own `PublishRecord` and source built in a headless
 * editor. The live editor is never mutated.
 *
 * Flow:
 *   1. Save the live editor (so the active AR's source is current).
 *   2. Expand the selection into `PublishCommandCell[]`.
 *   3. Create all records in one shot (shared `groupId` / `snapshotRef`).
 *   4. Track + mark rendering (legacy card "Publishing…" per AR).
 *   5. Per AR: populate resources → build source per language → dispatch.
 */

import * as EventTracker from '@src/analytics/EventTracker';
import { selectKaraokeVariantForLanguage } from '@src/editor/models/karaoke/selectKaraokeVariant';
import { correctKaraokeDataFonts } from '@src/editor/services/captionFontSubstitution';
import { VideoResolution } from '@src/types.ts/video';
import { normalizeLangKey } from '@src/utility/bcp47';
import {
  isOriginalLang,
  ORIGINAL_LANGUAGE,
  resolveSourceLanguageKey,
} from '@src/utility/captionLanguage';
import { extractErrorInfo } from '@src/utility/error';
import { FONTS } from '@src/utility/general';
import { LogManager } from '@src/utility/Logger';
import { computePublishFingerprints } from '@src/utility/publishFingerprints';

import type {
  FanoutSelection,
  IPublishFanoutDeps,
  PublishStemUrls,
} from './IPublishFanoutDeps';
import type { PublishCommandCell } from './PublishRecordService';
import type {
  PublishDubEntry,
  PublishSourceBase,
} from './PublishSourceBuilder';
import type {
  PublishFingerprints,
  PublishRecord,
} from '@src/types.ts/publishRecord';
import type { Video } from '@src/types.ts/story';
import type { AspectRatio, VideoSource } from '@src/types.ts/video';
import type { CompositionPhraseTranslation } from '@src/videoTranscriptionProcessor/translation/compositionTypes';

/** Shared empty map for original-language cells (no composition translations). */
const EMPTY_COMPOSITION_PHRASES: ReadonlyMap<
  string,
  CompositionPhraseTranslation
> = new Map();

/**
 * One AR's populated Video plus its derived `PublishSourceBase`. The base is
 * built once (in the populate pass) and reused for both fingerprinting and
 * source building, so `buildSourceBase` runs a single time per AR.
 */
interface PopulatedAr {
  video: Video;
  base: PublishSourceBase;
}

/** Rank resolutions highest-first for canonical cell selection. */
const RESOLUTION_RANK: Record<VideoResolution, number> = {
  [VideoResolution.HD]: 4,
  [VideoResolution.High]: 3,
  [VideoResolution.Medium]: 2,
  [VideoResolution.Low]: 1,
  [VideoResolution.Original]: 0,
};

export interface PublishFanoutResult {
  /** All records created for this command (in cell order). */
  records: PublishRecord[];
  /** Per-cell dispatch errors (index → error). Empty on full success. */
  dispatchErrors: Map<number, Error>;
}

/**
 * Stateless orchestrator — construct per publish command, call `execute`, done.
 * Dependencies are injected via `IPublishFanoutDeps` (wired by
 * `publishFanoutDepsFromStore`) so the service is unit-testable without stores.
 */
export class PublishFanoutService {
  private readonly logger = LogManager.getLogger(
    'service:PublishFanoutService',
  );

  constructor(private readonly deps: IPublishFanoutDeps) {}

  /**
   * Execute the full fan-out: save → expand → create records → build sources →
   * dispatch renders. Returns the created records and any per-cell dispatch
   * errors (records are created before dispatch, so partial failures are
   * possible — some cells may be `queued` with no render started).
   */
  async execute(selection: FanoutSelection): Promise<PublishFanoutResult> {
    const { deps } = this;

    if (!deps.story) {
      throw new Error('Cannot fan-out publish: story not set');
    }
    const storyId = deps.story.id;

    // 1. Persist the live editor's active AR so its source is current.
    this.logger.info('Saving live editor before fan-out');
    await deps.saveLiveEditor();

    // 2. Expand the matrix into cells.
    const cells = this.expandCells(selection);
    if (cells.length === 0) {
      this.logger.warn('Fan-out selection expanded to zero cells; aborting');
      return { records: [], dispatchErrors: new Map() };
    }
    this.logger.info('Fan-out expanded', { cellCount: cells.length });

    // 2b. Resolve one dub per selected non-original language, once per publish
    //     command. A dub belongs to the story and the language, not to an aspect
    //     ratio, so resolving it inside the per-AR populate would repeat the
    //     identical request once per ratio. Never throws: one language's failure
    //     cannot take down a publish of the others.
    const dubsByLanguage = await this.fetchDubsForLanguages(
      selection.languages,
    );

    // 2c. The kept music bed, once per publish command for the same reason: it
    //     belongs to the story, not to a language or an aspect ratio. It is
    //     built into each render document rather than read off the clip, so the
    //     render has to be handed the address the way the screen is.
    const stems = await this.fetchStemUrls();

    // 3. Populate each selected AR's resources once (best-effort). A failure
    //    here leaves the AR out of the map; its cells' records are created
    //    anyway and marked failed at dispatch (preserving today's semantics).
    const populatedByAr = await this.populateArResources(
      cells,
      dubsByLanguage,
      stems,
    );

    // 4. Derive each cell's per-AR record data (staleness fingerprint +
    //    snapshotRef) from its populated AR, so both persist atomically with the
    //    record (no extra Dato write). Cells whose AR failed to populate get
    //    null for both.
    this.assignPerCellRecordData(cells, populatedByAr);

    // 5. Create all PublishRecords in one shot (fingerprints included).
    let records: PublishRecord[];
    try {
      records = await deps.createRecordsForCommand(cells);
    } catch (err) {
      // Record creation is fatal — fire a failed event per intended cell.
      const errorMsg = err instanceof Error ? err.message : String(err);
      for (const cell of cells) {
        EventTracker.trackPublishFailed({
          story_id: storyId,
          group_id: '',
          publish_record_id: '',
          language: cell.language,
          aspect_ratio: cell.aspectRatio,
          resolution: cell.resolution,
          error_message: errorMsg,
        });
      }
      throw err;
    }

    const groupId = records[0]?.groupId ?? '';

    // Analytics: command submitted (records created, dispatches about to begin).
    EventTracker.trackPublishStarted({
      story_id: storyId,
      group_id: groupId,
      variant_count: records.length,
      languages: selection.languages,
      aspect_ratios: selection.aspectRatios,
      resolutions: selection.resLevels,
    });

    // 6. Track records (renderingVideoIds derives from the tracking store).
    deps.trackRecords(records);

    // 7. Build sources per AR × language × resolution and dispatch, reusing the
    //    resources populated in step 3.
    const dispatchErrors = await this.buildAndDispatch(
      selection,
      cells,
      records,
      populatedByAr,
    );

    return { records, dispatchErrors };
  }

  // ---------------------------------------------------------------------------
  // Private
  // ---------------------------------------------------------------------------

  /**
   * Expand `FanoutSelection` into the flat cell array. Each cell maps to one
   * `PublishRecord` and one render. `mirrorsToLegacy` is true on the per-AR
   * canonical cell (original language × that AR × highest selected resolution).
   */
  private expandCells(selection: FanoutSelection): PublishCommandCell[] {
    const { deps } = this;
    const cells: PublishCommandCell[] = [];
    const originalLang = deps.story?.originalVideoLanguage || 'en';
    const canonicalRes = [...selection.resLevels].sort(
      (a, b) => (RESOLUTION_RANK[b] ?? 0) - (RESOLUTION_RANK[a] ?? 0),
    )[0];

    for (const ar of selection.aspectRatios) {
      const video = deps.getVideoForAspectRatio(ar);
      if (!video?.id) {
        this.logger.warn('No video record for AR, skipping', {
          aspectRatio: ar,
        });
        continue;
      }

      for (const lang of selection.languages) {
        const isCanonical = isOriginalLang(lang);
        for (const res of selection.resLevels) {
          // Canonical: original language × this AR × highest resolution.
          const mirrors = isCanonical && res === canonicalRes;
          cells.push({
            videoId: video.id,
            language: isCanonical ? originalLang : lang,
            aspectRatio: ar,
            resolution: res,
            mirrorsToLegacy: mirrors,
            isOriginal: isCanonical,
          });
        }
      }
    }

    return cells;
  }

  /**
   * Build a source per cell (from its AR's pre-populated base) and dispatch its
   * render. Dispatches are independent — a failure in one cell does not abort
   * the others. A cell whose AR failed to populate (no map entry) is failed.
   */
  private async buildAndDispatch(
    selection: FanoutSelection,
    cells: PublishCommandCell[],
    records: PublishRecord[],
    populatedByAr: Map<string, PopulatedAr>,
  ): Promise<Map<number, Error>> {
    const { deps } = this;
    const errors = new Map<number, Error>();

    const songsData = deps.getSongsData();

    for (let idx = 0; idx < cells.length; idx++) {
      const cell = cells[idx];
      const record = records[idx];
      const resLevel = cell.resolution as VideoResolution;

      // Reuse the resources populated in step 3; a missing entry means populate
      // failed (or no video record) — fail this cell's record.
      const entry = populatedByAr.get(cell.aspectRatio);
      if (!entry) {
        const msg = `No populated resources for AR ${cell.aspectRatio}`;
        errors.set(idx, new Error(msg));
        await this.failCellRecord(record, msg);
        continue;
      }
      const { video, base } = entry;

      try {
        // Canonical cells store the raw original-language code in `cell.language`
        // (correct for the record), but the source builder must receive the
        // ORIGINAL_LANGUAGE sentinel so its `isOriginalLang` checks recognise it
        // (karaoke no-op + composition short-circuit).
        const builderLanguage = cell.isOriginal
          ? ORIGINAL_LANGUAGE
          : cell.language;
        const source = deps.buildSource(base, builderLanguage, resLevel);

        const sourceVideoUrl = deps.getSourceVideoUrl(resLevel);

        await this.dispatchRender({
          source,
          video,
          record,
          resLevel,
          renderUsingChunks: selection.renderUsingChunks,
          sourceVideoUrl,
          songsData,
          duration: source.duration,
        });
      } catch (err) {
        this.logger.error('Dispatch failed for cell', {
          cellIndex: idx,
          language: cell.language,
          aspectRatio: cell.aspectRatio,
          error: extractErrorInfo(err),
        });
        const error = err instanceof Error ? err : new Error(String(err));
        errors.set(idx, error);
        await this.failCellRecord(record, error.message);
      }
    }

    if (errors.size > 0) {
      this.logger.warn('Fan-out completed with dispatch errors', {
        total: cells.length,
        failed: errors.size,
      });
    }

    return errors;
  }

  /**
   * Resolve one dub per selected non-original language, in parallel.
   *
   * Keyed by `normalizeLangKey` — the same function the builder reads it back
   * with, because a lookup keyed on a non-normalised code silently misses and
   * the builder's fallback ("deliver the original recording") makes that miss
   * look like a plausible result rather than a bug.
   *
   * Three outcomes, kept apart: a resolved manifest is `ready`, no dub at all is
   * `none`, and a rejection is `error` for that language only. Never throws —
   * one bad language must not take down a publish of the others.
   */
  private async fetchDubsForLanguages(
    languages: string[],
  ): Promise<Record<string, PublishDubEntry>> {
    // A workspace with dubbing switched off delivers the original recording.
    // Gated on the workspace field only, never on the per-person feature flag: a
    // delivered file's audio must not depend on who pressed the button. Written
    // as an explicit `=== false` — the workspace record is filled
    // fire-and-forget, so treating a record that has not loaded yet as "off"
    // would silently drop the dub from an ordinary publish.
    if (this.deps.organization?.dubbingEnabled === false) {
      // A failed workspace load also reads as an explicit `false`, so a genuine
      // switch-off cannot be told apart from one here.
      this.logger.info(
        'Dubbing is switched off for this workspace; publishing the original recording',
        { requested: languages?.length ?? 0 },
      );
      return {};
    }

    const wanted = new Map<string, string>();
    for (const language of languages ?? []) {
      if (typeof language !== 'string' || isOriginalLang(language)) continue;
      const key = normalizeLangKey(language);
      if (!key || wanted.has(key)) continue;
      wanted.set(key, language);
    }

    const dubsByLanguage: Record<string, PublishDubEntry> = {};
    await Promise.all(
      [...wanted].map(async ([key, language]) => {
        try {
          const resolved = await this.deps.fetchDub(language);
          dubsByLanguage[key] = resolved
            ? {
                status: 'ready',
                manifest: resolved.manifest,
                renditionUrls: resolved.renditionUrls,
              }
            : { status: 'none' };
        } catch (err) {
          dubsByLanguage[key] = {
            status: 'error',
            message: err instanceof Error ? err.message : String(err),
          };
          this.logger.warn('Could not read a language’s dub for publish', {
            language,
            error: extractErrorInfo(err),
          });
        }
      }),
    );

    const counts = { ready: 0, none: 0, error: 0 };
    for (const entry of Object.values(dubsByLanguage)) counts[entry.status]++;
    this.logger.info('Resolved dubs for publish', {
      requested: wanted.size,
      ...counts,
    });

    return dubsByLanguage;
  }

  /**
   * Extract the `PublishSourceBase` from a populated Video record. After
   * `populateResources`, the Video has `videoSource`, `transcriptionSnapshot`,
   * `karaokeData`, `scenes`, and `extraElementData` hydrated.
   */
  private async fetchStemUrls(): Promise<PublishStemUrls> {
    try {
      return await this.deps.fetchStemUrls();
    } catch (err) {
      // Advisory: a render with no music under the dub beats a failed publish.
      this.logger.warn('Could not read the story’s music bed for publish', {
        error: extractErrorInfo(err),
      });
      return { bedUrl: null, voiceUrl: null };
    }
  }

  private buildSourceBase(
    video: Video,
    dubsByLanguage: Record<string, PublishDubEntry>,
    stems: PublishStemUrls,
  ): PublishSourceBase {
    const { deps } = this;
    // PLAT-11410 — correct the per-language variants ONCE here, exactly like
    // `KaraokeStore.hydrateLoadedByLanguage` does for the live editor: the
    // cell fingerprints hash this record, so it must carry the same corrected
    // data the source builder renders (otherwise a later editor-side
    // correction flips the captions hash and falsely flags the publish as
    // outdated). Entries are swapped in the record; variant objects are never
    // mutated.
    const byLanguage = video.karaokeDataByLanguage;
    if (byLanguage) {
      for (const [key, variant] of Object.entries(byLanguage)) {
        const corrected = correctKaraokeDataFonts(variant, key, {
          knownFamilies: FONTS,
        });
        if (corrected !== variant) byLanguage[key] = corrected;
      }
    }
    return {
      dubsByLanguage,
      musicBedUrl: stems.bedUrl,
      voiceStemUrl: stems.voiceUrl,
      videoSource: video.videoSource,
      transcriptElements: video.transcriptionSnapshot?.elements ?? [],
      originalTranscriptElements: deps.getOriginalTranscriptElements(),
      // try to fallback to the real base, not whatever is in karaokeData
      // (can be another translation)
      karaokeData:
        selectKaraokeVariantForLanguage(video.karaokeDataByLanguage, {
          isOriginal: true,
          langCode: deps.story?.originalVideoLanguage ?? 'en',
        }) ?? video.karaokeData,
      karaokeDataByLanguage: video.karaokeDataByLanguage,
      // PLAT-11410 — the canonical source key, so the original cell's produce
      // fallback can font-correct a non-Latin source language too.
      sourceLanguageKey: resolveSourceLanguageKey(
        null,
        deps.story?.originalVideoLanguage,
      ),
      extraElementData: video.extraElementData,
      scenes: video.scenes,
      originalVideoDuration: deps.story?.originalVideo?.video?.duration
        ? String(deps.story.originalVideo.video.duration)
        : undefined,
    };
  }

  /**
   * Populate each selected AR's Video resources once, keyed by AR, and derive
   * its `PublishSourceBase` a single time. Best-effort: a missing video record
   * or a populate failure omits that AR (its cells fail at dispatch). Never
   * throws.
   */
  private async populateArResources(
    cells: PublishCommandCell[],
    dubsByLanguage: Record<string, PublishDubEntry>,
    stems: PublishStemUrls,
  ): Promise<Map<string, PopulatedAr>> {
    const { deps } = this;
    const populated = new Map<string, PopulatedAr>();
    const ars = new Set(cells.map(c => c.aspectRatio));
    for (const ar of ars) {
      const video = deps.getVideoForAspectRatio(ar as AspectRatio);
      if (!video) {
        this.logger.warn('No video record for AR; cells will fail', {
          aspectRatio: ar,
        });
        continue;
      }
      try {
        const populatedVideo = await deps.populateResources(video);
        populated.set(ar, {
          video: populatedVideo,
          base: this.buildSourceBase(populatedVideo, dubsByLanguage, stems),
        });
      } catch (err) {
        this.logger.error('Failed to populate resources for AR', {
          aspectRatio: ar,
          error: extractErrorInfo(err),
        });
      }
    }
    return populated;
  }

  /**
   * Derive each cell's per-AR record data from its populated AR: the staleness
   * fingerprint (base + per-language slices) and the `snapshotRef` provenance
   * map (that AR's own `resourcesMetadata` — per-AR, NOT a shared command value).
   * Cells whose AR failed to populate (or whose fingerprint compute throws) get
   * null — never fatal to the publish.
   */
  private assignPerCellRecordData(
    cells: PublishCommandCell[],
    populatedByAr: Map<string, PopulatedAr>,
  ): void {
    for (const cell of cells) {
      const populated = populatedByAr.get(cell.aspectRatio);
      if (!populated) {
        cell.fingerprints = null;
        cell.snapshotRef = null;
        continue;
      }
      cell.snapshotRef = populated.video.resourcesMetadata ?? null;
      try {
        cell.fingerprints = this.computeCellFingerprints(populated.base, cell);
      } catch (err) {
        this.logger.warn('Failed to compute fingerprint for cell', {
          aspectRatio: cell.aspectRatio,
          language: cell.language,
          error: extractErrorInfo(err),
        });
        cell.fingerprints = null;
      }
    }
  }

  /** Assemble a cell's fingerprint inputs (per-AR base + language slices). */
  private computeCellFingerprints(
    base: PublishSourceBase,
    cell: PublishCommandCell,
  ): PublishFingerprints {
    return computePublishFingerprints({
      videoSource: base.videoSource,
      transcriptElements: base.transcriptElements,
      // PLAT-10788 — hash the per-language variant that will be rendered, so
      // staleness is per-language. Kept in lockstep with the live side
      // (`useCurrentPublishFingerprints`) so the two never diverge.
      karaokeData:
        selectKaraokeVariantForLanguage(base.karaokeDataByLanguage, {
          isOriginal: cell.isOriginal,
          langCode: cell.language,
        }) ??
        base.karaokeData ??
        null,
      // Original cells render from the transcript, not a translation — mirror
      // the builder's original short-circuit (no translation / composition).
      translationPhrases: cell.isOriginal
        ? null
        : this.deps.getTranslationPhrases(cell.language),
      compositionPhrases: cell.isOriginal
        ? EMPTY_COMPOSITION_PHRASES
        : this.deps.getCompositionPhrases(cell.language),
      scenes: base.scenes ?? null,
    });
  }

  /**
   * Reconcile a cell whose render was never dispatched: persist the record's
   * terminal `failed` state (through the service, so a refresh's in-flight seed
   * won't resurrect it) and prune it from in-memory tracking so the card stops
   * showing "Publishing…". The in-memory prune runs even if the Dato write fails
   * (worst case: a refresh re-seeds it, which is logged — no regression).
   */
  private async failCellRecord(
    record: PublishRecord | undefined,
    message: string,
  ): Promise<void> {
    if (!record) return;
    try {
      await this.deps.markRecordFailed(record.id, message);
    } catch (err) {
      this.logger.error('Failed to persist publish-record failure', {
        recordId: record.id,
        error: extractErrorInfo(err),
      });
    }
    this.deps.markDispatchFailed(record.id, message);
  }

  /** Dispatch one render via the per-variant `VideoPublisher`. */
  private async dispatchRender(params: {
    source: VideoSource;
    video: Video;
    record: PublishRecord;
    resLevel: VideoResolution;
    renderUsingChunks: boolean;
    sourceVideoUrl: string;
    songsData: unknown[];
    duration: number;
  }): Promise<void> {
    const { deps } = this;
    const {
      source,
      video,
      record,
      resLevel,
      renderUsingChunks,
      sourceVideoUrl,
      songsData,
      duration,
    } = params;

    const publisher = deps.createPublisher(video);
    await publisher.publish({
      source,
      video,
      resLevel,
      renderUsingChunks,
      sourceVideoUrl,
      duration,
      songsData,
      publishRecordId: record.id,
      groupId: record.groupId,
      mirrorsToLegacy: record.mirrorsToLegacy,
    });
  }
}
